import { Game, GameType } from "@aura-frontend/omnibus-az-client";
import { toast } from "@win-reality/react-ui";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { EventsTable } from "@/components/EventsTable";
import { MachineSelector } from "@/components/MachineSelector";
import { AlertMessage } from "@/components/ui/AlertMessage";
import { useDateRange } from "@/hooks/useDateRange";
import { useDownloadGameCsv } from "@/hooks/useDownloadGameCsv";
import { useEventFilters } from "@/hooks/useEventFilters";
import { useGamesFromMachine } from "@/hooks/useGamesFromMachine";
import { useMachineSelection } from "@/hooks/useMachineSelection";

import { GenerateReportModal } from "./components/GenerateReportModal";
import { useGenerateReport } from "./hooks/useGenerateReport";

export const FindEventLog = () => {
  const navigate = useNavigate();
  const [reportModalGame, setReportModalGame] = useState<Game | null>(null);
  const [generatedReportGameIds, setGeneratedReportGameIds] = useState<
    Set<string>
  >(new Set());

  const {
    selectedEventType,
    setSelectedEventType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    endTime,
    resetFilters,
  } = useEventFilters();

  const { selectedMachineId, handleMachineChange } = useMachineSelection({
    onMachineChange: () => {
      resetFilters();
    },
  });

  const { downloadCsv, downloadingGameId } = useDownloadGameCsv({
    onError: (msg) => toast(msg, { variant: "error" }),
  });

  const { generateReport, generatingReportGameIds } = useGenerateReport();

  const {
    games,
    isLoading: loadingGames,
    error: gamesError,
  } = useGamesFromMachine({
    machineUuid: selectedMachineId || undefined,
    gameType:
      selectedEventType !== "all" ? (selectedEventType as GameType) : undefined,
    startTime,
    endTime,
  });

  const handleEdit = (game: Game) => {
    const gameId = game.gameUuid;
    if (gameId) {
      navigate(`/event-log/${gameId}`);
    } else {
      console.warn("No gameId found in game object");
    }
  };

  const addToGeneratedReports = (gameUuid: string) => {
    setGeneratedReportGameIds((prev) => {
      const next = new Set(prev);
      next.add(gameUuid);
      return next;
    });
  };

  const handleGenerateReport = async (game: Game) => {
    if (game.ageGroup) {
      const success = await generateReport(game, game.ageGroup);
      if (success && game.gameUuid) {
        addToGeneratedReports(game.gameUuid);
      }
    } else {
      setReportModalGame(game);
    }
  };

  const { dateRange, handleDateRangeChange } = useDateRange({
    initialFrom: startDate || undefined,
    initialTo: endDate || undefined,
    onFromChange: (v) => setStartDate(v ?? ""),
    onToChange: (v) => setEndDate(v ?? ""),
  });

  return (
    <div className="mx-auto py-6 space-y-6 px-16">
      <MachineSelector
        showRegionSelector={false}
        selectedMachineId={selectedMachineId}
        onMachineChange={handleMachineChange}
      />

      {selectedMachineId && (
        <div>
          {gamesError ? (
            <AlertMessage
              variant="destructive"
              title="Unable to load events"
              description="There was a problem loading events for the selected machine. Please try selecting a different machine or refresh the page."
            />
          ) : (
            <>
              <EventsTable
                games={games ?? []}
                isLoading={loadingGames}
                onEdit={handleEdit}
                onDownload={downloadCsv}
                onGenerateReport={handleGenerateReport}
                downloadingGameId={downloadingGameId}
                generatingReportGameIds={generatingReportGameIds}
                selectedEventType={selectedEventType}
                onEventTypeChange={setSelectedEventType}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                generatedReportGameIds={generatedReportGameIds}
              />
              <GenerateReportModal
                open={!!reportModalGame}
                onOpenChange={(open) => !open && setReportModalGame(null)}
                game={reportModalGame}
                onSuccess={addToGeneratedReports}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FindEventLog;
